// The handful of primitives the whole interface is built from. They are small
// on purpose: a component library is not the point, consistency is.
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { PAGE_SIZES, type SortDir, type TableState } from "@/lib/table";

// --- button --------------------------------------------------------------

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
};

export function Button({
  className,
  variant = "secondary",
  size = "md",
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap",
        "transition-[background-color,box-shadow,color] duration-100 active:translate-y-px",
        "disabled:pointer-events-none disabled:opacity-50",
        size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-sm",
        variant === "primary" &&
          "bg-brand-600 text-white shadow-xs hover:bg-brand-700",
        variant === "secondary" &&
          "border surface shadow-xs hover:bg-[var(--surface-sunken)]",
        variant === "ghost" &&
          "muted hover:bg-[var(--surface-sunken)] hover:text-[var(--text)]",
        variant === "danger" &&
          "bg-red-600 text-white shadow-xs hover:bg-red-700",
        className,
      )}
      {...props}
    />
  );
}

// --- form fields ---------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium muted">{label}</span>
      {children}
      {error ? (
        <span className="text-xs text-red-600 dark:text-red-400">{error}</span>
      ) : hint ? (
        <span className="text-xs muted">{hint}</span>
      ) : null}
    </label>
  );
}

export function Input({
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        "h-8 w-full rounded-md border surface px-2.5 text-sm shadow-xs",
        "placeholder:text-[var(--text-muted)]",
        "transition-[border-color,box-shadow] duration-100",
        "focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--ring)]",
        "disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

export function Select({
  className,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        "h-8 w-full rounded-md border surface px-2 text-sm shadow-xs",
        "transition-[border-color,box-shadow] duration-100",
        "focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--ring)]",
        "disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

// A select that can be searched. A plain dropdown is fine for a handful of
// options; with hundreds of look-alike company names it is a scrolling
// exercise, so this one filters as you type and answers to the keyboard.

export type ComboboxItem = {
  value: number | string;
  label: string;
  /** A second line, shown and searched: what tells look-alikes apart. */
  hint?: string;
};

/** How many matches we draw. Beyond that the list is not read, it is scrolled. */
const COMBOBOX_LIMIT = 100;

/** Lowercase, without accents: "Müller" and "Muller" are the same search. */
function foldCase(text: string): string {
  return text
    .toLowerCase()
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function matchItems(
  items: ComboboxItem[],
  query: string,
): { shown: ComboboxItem[]; total: number } {
  const terms = foldCase(query).split(/\s+/).filter(Boolean);
  const first = terms[0];
  if (first === undefined) {
    return { shown: items.slice(0, COMBOBOX_LIMIT), total: items.length };
  }

  const hits: { item: ComboboxItem; rank: number }[] = [];
  for (const item of items) {
    const label = foldCase(item.label);
    const haystack = item.hint ? `${label} ${foldCase(item.hint)}` : label;
    if (!terms.every((t) => haystack.includes(t))) continue;
    // What begins with what was typed is what was meant, most of the time.
    const rank = label.startsWith(first) ? 0 : label.includes(first) ? 1 : 2;
    hits.push({ item, rank });
  }
  const shown = hits
    .sort((a, b) => a.rank - b.rank)
    .slice(0, COMBOBOX_LIMIT)
    .map((h) => h.item);
  return { shown, total: hits.length };
}

export function Combobox({
  value,
  items,
  onChange,
  className,
  placeholder = "wählen",
  searchPlaceholder = "suchen …",
  emptyLabel = "keine Treffer",
  label,
  disabled,
}: {
  value: number | string;
  items: ComboboxItem[];
  onChange: (value: number | string) => void;
  className?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  /** Accessible name, for the places that have no visible <Field> label. */
  label?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();

  const selected = items.find((i) => i.value === value);
  const { shown: matches, total } = useMemo(
    () => matchItems(items, query),
    [items, query],
  );

  // Clicking anywhere else means: not this one.
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  // Opening starts on the current value, closing forgets the search.
  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    search.current?.focus();
    setActive(
      Math.max(
        0,
        items.findIndex((i) => i.value === value),
      ),
    );
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    if (!open) return;
    list.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const choose = (item: ComboboxItem) => {
    onChange(item.value);
    setOpen(false);
    trigger.current?.focus();
  };

  return (
    <div ref={root} className={cn("relative", className)}>
      <button
        ref={trigger}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        className={cn(
          "flex h-8 w-full items-center justify-between gap-2 rounded-md border surface px-2.5 text-sm shadow-xs",
          "transition-[border-color,box-shadow] duration-100",
          "focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--ring)]",
          "disabled:opacity-50",
        )}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className={cn("truncate", !selected && "muted")}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronsUpDown className="size-3.5 shrink-0 muted" aria-hidden />
      </button>

      {open && (
        <div
          className="absolute left-0 z-50 mt-1 w-full min-w-64 rounded-lg border surface p-1 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            ref={search}
            value={query}
            placeholder={searchPlaceholder}
            aria-controls={`${id}-list`}
            aria-activedescendant={
              matches[active] ? `${id}-opt-${active}` : undefined
            }
            className="mb-1 h-7 w-full rounded-md border bg-transparent px-2 text-sm placeholder:text-[var(--text-muted)] focus-visible:border-brand-500 focus-visible:outline-none"
            style={{ borderColor: "var(--border)" }}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, matches.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                const hit = matches[active];
                if (hit) choose(hit);
              } else if (e.key === "Escape" || e.key === "Tab") {
                e.preventDefault();
                setOpen(false);
                trigger.current?.focus();
              }
            }}
          />

          <ul
            ref={list}
            id={`${id}-list`}
            role="listbox"
            aria-label={label}
            className="max-h-72 overflow-y-auto"
          >
            {matches.length === 0 && (
              <li className="px-2 py-3 text-sm muted">{emptyLabel}</li>
            )}
            {matches.map((item, i) => (
              <li
                key={item.value}
                id={`${id}-opt-${i}`}
                role="option"
                aria-selected={item.value === value}
                // The search field keeps the focus, so the click has to not steal it.
                onPointerDown={(e) => e.preventDefault()}
                onClick={() => choose(item)}
                onMouseMove={() => setActive(i)}
                className={cn(
                  "cursor-pointer rounded-md px-2 py-1 text-sm",
                  i === active && "bg-[var(--surface-sunken)]",
                  item.value === value && "font-medium",
                )}
              >
                <span className="block truncate">{item.label}</span>
                {item.hint && (
                  <span className="block truncate text-xs muted">
                    {item.hint}
                  </span>
                )}
              </li>
            ))}
          </ul>

          {total > matches.length && (
            <div className="px-2 py-1.5 text-xs muted">
              {matches.length} von {total} Treffern — Suche verfeinern
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- surfaces ------------------------------------------------------------

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border surface shadow-xs",
        className,
      )}
      {...props}
    />
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-0.5 text-sm muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

// --- status --------------------------------------------------------------

const tones = {
  neutral:
    "bg-[var(--surface-sunken)] muted ring-1 ring-inset ring-[var(--border)]",
  info: "bg-brand-100 text-brand-700 dark:bg-brand-700/25 dark:text-brand-100",
  success:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200",
  warning:
    "bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100",
  danger: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200",
} as const;

export type Tone = keyof typeof tones;

export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: Tone;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-1.5 py-px text-xs font-medium leading-4 whitespace-nowrap",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

// --- feedback ------------------------------------------------------------

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 px-6 py-12 text-center">
      <p className="font-medium">{title}</p>
      {hint && <p className="text-sm muted">{hint}</p>}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
      {children}
    </div>
  );
}

export function Spinner({ label = "wird geladen" }: { label?: string }) {
  return (
    <div
      className="flex items-center gap-2 px-6 py-8 text-sm muted"
      role="status"
    >
      <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      {label}…
    </div>
  );
}

// --- table ---------------------------------------------------------------

export function Table({ children, dense }: { children: ReactNode; dense?: boolean }) {
  // The wrapper scrolls rather than the page, so a wide table never pushes the
  // layout sideways on a narrow screen. A dense table trims the cell padding
  // so that a wide listing still fits a narrower window.
  return (
    <div className="overflow-x-auto">
      <table
        className={cn("data-table w-full border-collapse text-sm", dense && "data-table-dense")}
      >
        {children}
      </table>
    </div>
  );
}

export function Th({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  return (
    <th
      className={cn(
        "border-b px-3 py-1.5 text-left text-[0.6875rem] font-semibold uppercase tracking-wider muted whitespace-nowrap",
        className,
      )}
      style={{ borderColor: "var(--border)" }}
    >
      {children}
    </th>
  );
}

/**
 * A column header that sorts. The button carries the whole header so that it
 * is reachable by keyboard and announced as a control; aria-sort on the cell
 * is what a screen reader uses to say which way the table is turned.
 *
 * `first` is the direction the first click chooses. A date or a count reads
 * newest-or-largest first, so those columns pass "desc"; a name reads A to Z.
 * A third click restores the listing's own order.
 */
export function SortableTh({
  table,
  sortKey,
  first = "asc",
  align = "left",
  className,
  children,
}: {
  table: TableState;
  sortKey: string;
  first?: SortDir;
  align?: "left" | "right";
  className?: string;
  children?: ReactNode;
}) {
  const active = table.sort?.key === sortKey;
  const dir = active ? table.sort!.dir : undefined;

  return (
    <th
      aria-sort={
        dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none"
      }
      className={cn(
        "border-b p-0 text-[0.6875rem] font-semibold uppercase tracking-wider muted whitespace-nowrap",
        className,
      )}
      style={{ borderColor: "var(--border)" }}
    >
      <button
        type="button"
        onClick={() => table.toggle(sortKey, first)}
        title={
          active
            ? dir === "asc"
              ? "aufsteigend sortiert — klicken für absteigend"
              : "absteigend sortiert — klicken für die Standardreihenfolge"
            : "nach dieser Spalte sortieren"
        }
        className={cn(
          "flex w-full items-center gap-1 px-3 py-1.5 text-left uppercase",
          "hover:text-[var(--text)] focus-visible:outline-2",
          align === "right" && "justify-end",
          active && "text-[var(--text)]",
        )}
      >
        <span className={cn(align === "right" && "order-2")}>{children}</span>
        <SortMark dir={dir} />
      </button>
    </th>
  );
}

function SortMark({ dir }: { dir?: SortDir }) {
  const cls = "size-3.5 shrink-0";
  if (dir === "asc") return <ArrowUp className={cls} aria-hidden />;
  if (dir === "desc") return <ArrowDown className={cls} aria-hidden />;
  // The inactive columns keep their marker, faint: without it the sortable
  // ones are indistinguishable from the fixed ones until they are clicked.
  return <ChevronsUpDown className={cn(cls, "opacity-25")} aria-hidden />;
}

export function Td({
  children,
  className,
  title,
  colSpan,
}: {
  children?: ReactNode;
  className?: string;
  title?: string;
  colSpan?: number;
}) {
  return (
    <td
      className={cn("border-b px-3 py-[7px] align-middle", className)}
      style={{ borderColor: "var(--border)" }}
      title={title}
      colSpan={colSpan}
    >
      {children}
    </td>
  );
}

// --- dialog --------------------------------------------------------------

const dialogWidths = {
  sm: "max-w-lg",
  lg: "max-w-3xl",
  xl: "max-w-6xl",
} as const;

export function Dialog({
  open,
  onClose,
  title,
  size = "sm",
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  // Forms stay narrow; anything with a table or a script in it wants room.
  size?: keyof typeof dialogWidths;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-[2px] sm:p-8"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={cn("w-full rounded-xl border surface p-5 shadow-2xl", dialogWidths[size])}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold tracking-tight">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            title="Schließen"
            className="-mr-1 -mt-1 rounded-md p-1 muted transition-colors hover:bg-[var(--surface-hover)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--ring)]"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// --- more form fields ----------------------------------------------------

export function Textarea({
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(
        "min-h-20 w-full rounded-md border surface px-2.5 py-1.5 text-sm shadow-xs",
        "placeholder:text-[var(--text-muted)]",
        "transition-[border-color,box-shadow] duration-100",
        "focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--ring)]",
        className,
      )}
      {...props}
    />
  );
}

export function Checkbox({
  label,
  hint,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return (
    <label className="flex items-start gap-2 py-0.5">
      <input
        type="checkbox"
        className={cn("mt-0.5 size-3.5 rounded border", className)}
        {...props}
      />
      <span>
        <span className="text-sm">{label}</span>
        {hint && <span className="block text-xs muted">{hint}</span>}
      </span>
    </label>
  );
}

// --- tabs ----------------------------------------------------------------

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: string; label: string; badge?: number }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    // The rule and the scrolling strip are two boxes: the strip hangs one pixel
    // over the rule so the active tab's underline covers it, and a strip that
    // scrolls sideways must not be the box that overhangs, or the overhang
    // becomes a one-pixel vertical scrollbar.
    <div className="mb-3 border-b" style={{ borderColor: "var(--border)" }}>
      <div className="-mb-px flex gap-0.5 overflow-x-auto" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={active === t.id}
            onClick={() => onChange(t.id)}
            className={cn(
              "shrink-0 border-b-2 px-2.5 py-1.5 text-sm transition-colors",
              active === t.id
                ? "border-brand-600 font-medium text-brand-700 dark:text-brand-100"
                : "border-transparent muted hover:border-[var(--border-strong)] hover:text-[var(--text)]",
            )}
          >
            {t.label}
            {t.badge != null && t.badge > 0 && (
              <span className="ml-1.5 rounded-md bg-[var(--surface-sunken)] px-1.5 py-px text-xs tabular-nums ring-1 ring-inset ring-[var(--border)]">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// --- toolbar -------------------------------------------------------------

/** The row of filters and actions above a table. */
export function Toolbar({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2.5 flex flex-wrap items-center gap-2">{children}</div>
  );
}

// --- confirmation --------------------------------------------------------

/**
 * A yes/no question before something irreversible. With `challenge` set, the
 * confirm button stays disabled until that exact text has been typed — for
 * the deletions that take a whole customer with them.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Löschen",
  challenge,
  challengeLabel,
  error,
  onConfirm,
  onClose,
  busy,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  /** The text the user has to type before confirming, e.g. the record's name. */
  challenge?: string;
  challengeLabel?: string;
  /** What went wrong on the last attempt; shown inside the dialog. */
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const [typed, setTyped] = useState("");
  // A fresh dialog starts with an empty field, whichever record it was
  // opened for last.
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  const armed = challenge === undefined || typed.trim() === challenge.trim();

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <div className="text-sm">{message}</div>
      {challenge !== undefined && (
        <div className="mt-4">
          <Field
            label={
              challengeLabel ??
              `Zur Bestätigung „${challenge}" eingeben`
            }
          >
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={challenge}
              autoComplete="off"
              spellCheck={false}
              autoFocus
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === "Enter" && armed && !busy) onConfirm();
              }}
            />
          </Field>
        </div>
      )}
      {error && (
        <div className="mt-4">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button onClick={onClose} disabled={busy}>
          Abbrechen
        </Button>
        <Button variant="danger" onClick={onConfirm} disabled={busy || !armed}>
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}

// --- audio ---------------------------------------------------------------

/**
 * The one player used everywhere: preview, building block and the recording of
 * a robot call. It is a plain audio element on purpose — it gets range
 * requests, seeking and the platform's own controls for nothing.
 */
export function AudioPlayer({
  src,
  label,
  className,
}: {
  src: string;
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {label && <span className="text-xs muted">{label}</span>}
      <audio
        src={src}
        controls
        preload="none"
        className="h-9 w-full max-w-md"
      />
    </div>
  );
}

// --- notices -------------------------------------------------------------

export function Notice({
  tone = "info",
  children,
}: {
  tone?: "info" | "success" | "warning";
  children: ReactNode;
}) {
  const styles = {
    info: "border-brand-300 bg-brand-50 text-brand-900 dark:border-brand-800 dark:bg-brand-950/40 dark:text-brand-100",
    success:
      "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100",
    warning:
      "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100",
  } as const;
  return (
    <div className={cn("rounded-lg border px-3 py-2 text-sm", styles[tone])}>
      {children}
    </div>
  );
}

/** A number with a label, used across the dashboard and the reports. */
export function Stat({
  label,
  value,
  tone = "neutral",
  hint,
}: {
  label: string;
  value: ReactNode;
  tone?: Tone;
  hint?: string;
}) {
  return (
    <Card className="px-3.5 py-3">
      <div className="text-[0.6875rem] font-medium uppercase tracking-wider muted">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tracking-tight tabular-nums">
          {value}
        </span>
        {tone !== "neutral" && <Badge tone={tone}>{toneLabel(tone)}</Badge>}
      </div>
      {hint && <div className="mt-1 text-xs muted">{hint}</div>}
    </Card>
  );
}

function toneLabel(tone: Tone): string {
  switch (tone) {
    case "success":
      return "ok";
    case "warning":
      return "prüfen";
    case "danger":
      return "Fehler";
    default:
      return "";
  }
}

// --- pagination ----------------------------------------------------------

/**
 * The strip under a table: what is being shown, how to move through it, and
 * how much of it to show at once.
 *
 * It is rendered for every listing, including the short ones, so that "1–7 von
 * 7" is always there to be read. The page buttons appear only when there is
 * more than one page — a control that can never do anything is noise.
 */
export function TableFooter({
  table,
  noun = "Einträge",
}: {
  table: TableState;
  noun?: string;
}) {
  const { total, offset, pageSize, page, pageCount } = table;
  if (total === 0) return null;

  const from = offset + 1;
  const to = Math.min(offset + pageSize, total);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 border-t bg-[var(--surface-sunken)]/60 px-3 py-1.5"
      style={{ borderColor: "var(--border)" }}
    >
      <span className="text-xs muted tabular-nums">
        {from}–{to} von {total} {noun}
      </span>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs muted">
          pro Seite
          <Select
            className="h-6 w-auto py-0 text-xs shadow-none"
            value={pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            aria-label="Einträge pro Seite"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </label>

        {pageCount > 1 && (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              className="px-1.5"
              disabled={page === 0}
              onClick={() => table.goto(0)}
              aria-label="erste Seite"
            >
              <ChevronsLeft className="size-4" aria-hidden />
            </Button>
            <Button
              size="sm"
              className="px-1.5"
              disabled={page === 0}
              onClick={() => table.goto(page - 1)}
              aria-label="vorige Seite"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
            <span className="px-1 text-xs muted tabular-nums">
              {page + 1} / {pageCount}
            </span>
            <Button
              size="sm"
              className="px-1.5"
              disabled={page + 1 >= pageCount}
              onClick={() => table.goto(page + 1)}
              aria-label="nächste Seite"
            >
              <ChevronRight className="size-4" aria-hidden />
            </Button>
            <Button
              size="sm"
              className="px-1.5"
              disabled={page + 1 >= pageCount}
              onClick={() => table.goto(pageCount - 1)}
              aria-label="letzte Seite"
            >
              <ChevronsRight className="size-4" aria-hidden />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
